import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  FaBan,
  FaBuilding,
  FaCalendarAlt,
  FaChevronDown,
  FaClock,
  FaCreditCard,
  FaDesktop,
  FaEnvelope,
  FaFileAlt,
  FaGavel,
  FaHeadset,
  FaInfoCircle,
  FaLock,
  FaShieldAlt,
  FaTruck,
  FaUser,
  FaExclamationTriangle,
  FaIndustry,
  FaClipboardCheck,
  FaBalanceScale,
  FaCogs,
  FaUserCheck,
} from "react-icons/fa";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import HubHomeFooter from "../../components/hub/HubHomeFooter";
import TermsHeroVisual from "../../components/terms/TermsHeroVisual";
import {
  TERMS_MODULE_CALLOUTS,
  TERMS_MODULE_INTRO,
  TERMS_MODULE_NAV_LABELS,
  TERMS_MODULE_NAV_OPTIONS,
  TERMS_MODULE_PAGE_TITLES,
} from "../../constants/cms";
import { cmsExtractMessage, fetchPublicTerms } from "../../services/cms.service";
import { buildTermsSectionsFromContent } from "../../utils/termsModuleCms";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/cms_terms_public.scss";

const SECTION_ICONS = {
  file: FaFileAlt,
  user: FaUser,
  gavel: FaGavel,
  industry: FaIndustry,
  truck: FaTruck,
  shield: FaShieldAlt,
  desktop: FaDesktop,
  card: FaCreditCard,
  ban: FaBan,
  warning: FaExclamationTriangle,
  lock: FaLock,
  envelope: FaEnvelope,
};

const MODULE_NAV_ICONS = {
  PLATFORM: FaBuilding,
  AUCTION: FaGavel,
  INFRASTRUCTURE_LEASING: FaIndustry,
  CONTRACT_MANUFACTURING: FaCogs,
  PROCURE_TO_PAY: FaClipboardCheck,
  QUALITY_ASSESSMENT: FaUserCheck,
  CONSULTANTS: FaUser,
  LEGAL: FaBalanceScale,
  LOGISTICS: FaTruck,
};

const HERO_VARIANTS = {
  PLATFORM: "platform",
  AUCTION: "auction",
  INFRASTRUCTURE_LEASING: "infrastructure",
  CONTRACT_MANUFACTURING: "manufacturing",
  PROCURE_TO_PAY: "p2p",
  QUALITY_ASSESSMENT: "quality",
  CONSULTANTS: "consultants",
  LEGAL: "legal",
  LOGISTICS: "logistics",
};

function formatBadgeDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

function fallbackTodayLabel() {
  return new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function PublicTermsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedModule = String(searchParams.get("module") || "PLATFORM").toUpperCase();
  const moduleName = TERMS_MODULE_NAV_LABELS[requestedModule] ? requestedModule : "PLATFORM";
  const [loading, setLoading] = useState(true);
  const [terms, setTerms] = useState(null);
  const [error, setError] = useState("");
  const [openIds, setOpenIds] = useState(() => new Set(["introduction"]));
  const requestIdRef = useRef(0);

  const loadTerms = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError("");
    setTerms(null);
    try {
      const data = await fetchPublicTerms(moduleName);
      if (requestId !== requestIdRef.current) return;

      const responseModule = String(data?.moduleName || "").toUpperCase();
      if (data && responseModule && responseModule !== moduleName) {
        setTerms(null);
        setError(`Showing standard ${TERMS_MODULE_NAV_LABELS[moduleName]} terms.`);
        return;
      }

      setTerms(data);
      if (!data) {
        setError(`Showing standard ${TERMS_MODULE_NAV_LABELS[moduleName]} terms.`);
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(
        cmsExtractMessage(
          err,
          `Showing standard ${TERMS_MODULE_NAV_LABELS[moduleName]} terms. Live content is temporarily unavailable.`,
        ),
      );
      setTerms(null);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [moduleName]);

  useEffect(() => {
    loadTerms();
  }, [loadTerms]);

  const moduleNavLabel = TERMS_MODULE_NAV_LABELS[moduleName] || moduleName;
  const pageTitle =
    TERMS_MODULE_PAGE_TITLES[moduleName] || `${moduleNavLabel} – Terms & Conditions`;
  const moduleIntro = TERMS_MODULE_INTRO[moduleName] || TERMS_MODULE_INTRO.PLATFORM;
  const calloutText =
    TERMS_MODULE_CALLOUTS[moduleName] ||
    `These ${moduleNavLabel} terms are additional to the Overall Platform Terms & Conditions and apply when you use this module.`;
  const heroVariant = HERO_VARIANTS[moduleName] || "default";

  const sections = useMemo(
    () =>
      buildTermsSectionsFromContent(terms?.content, {
        moduleName,
        moduleLabel: moduleNavLabel,
      }),
    [terms?.content, moduleName, moduleNavLabel],
  );

  const lastUpdated = formatBadgeDate(terms?.updatedAt) || fallbackTodayLabel();
  const effectiveDate = formatBadgeDate(terms?.effectiveDate) || lastUpdated;

  useEffect(() => {
    const ids = sections.map((s) => s.id);
    setOpenIds(new Set(ids));
  }, [sections]);

  const selectModule = (value) => {
    setSearchParams(value === "PLATFORM" ? {} : { module: value });
  };

  const toggleSection = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="hub-terms-page">
      <B2BHubHomeHeader />

      <main className="hub-terms-page__main">
        <section className="hub-terms-hero" aria-labelledby="hub-terms-title">
          <div className="container-fluid hub-terms-hero__inner">
            <div className="hub-terms-hero__copy">
              <nav className="hub-terms-breadcrumb" aria-label="Breadcrumb">
                <Link to="/home">Home</Link>
                <span aria-hidden>&gt;</span>
                <span>Policies</span>
                <span aria-hidden>&gt;</span>
                <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>
                <span aria-hidden>&gt;</span>
                <span aria-current="page">{moduleNavLabel}</span>
              </nav>

              <h1 id="hub-terms-title" className="hub-terms-hero__title">
                {pageTitle}
              </h1>
              <p className="hub-terms-hero__lead">{moduleIntro}</p>

              <div className="hub-terms-hero__meta">
                <span className="hub-terms-hero__meta-item">
                  <FaCalendarAlt aria-hidden />
                  <span>
                    <strong>Effective Date:</strong> {effectiveDate}
                  </span>
                </span>
                <span className="hub-terms-hero__meta-sep" aria-hidden>
                  |
                </span>
                <span className="hub-terms-hero__meta-item">
                  <FaClock aria-hidden />
                  <span>
                    <strong>Last Updated:</strong> {lastUpdated}
                  </span>
                </span>
              </div>
            </div>

            <TermsHeroVisual variant={heroVariant} />
          </div>
        </section>

        <section className="hub-terms-body">
          <div className="container-fluid hub-terms-body__inner hub-terms-body__inner--two-col">
            <aside className="hub-terms-modules" aria-label="Modules">
              <h2 className="hub-terms-modules__heading">Modules</h2>
              <nav className="hub-terms-modules__nav">
                {TERMS_MODULE_NAV_OPTIONS.map((opt) => {
                  const Icon = MODULE_NAV_ICONS[opt.value] || FaFileAlt;
                  const isActive = moduleName === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={`hub-terms-modules__link${isActive ? " is-active" : ""}`}
                      aria-current={isActive ? "page" : undefined}
                      onClick={() => selectModule(opt.value)}
                    >
                      <span className="hub-terms-modules__icon" aria-hidden>
                        <Icon />
                      </span>
                      <span>{opt.label}</span>
                    </button>
                  );
                })}
              </nav>

              <div className="hub-terms-help">
                <span className="hub-terms-help__icon" aria-hidden>
                  <FaHeadset />
                </span>
                <p className="hub-terms-help__title">Need Help?</p>
                <p className="hub-terms-help__sub">
                  Have questions about these terms? Our support team is here to help.
                </p>
                <Link to="/contact" className="hub-terms-help__btn">
                  Contact Support
                  <span aria-hidden>→</span>
                </Link>
              </div>
            </aside>

            <div className="hub-terms-content">
              {loading ? (
                <p className="hub-terms-content__status">Loading terms...</p>
              ) : (
                <>
                  {error ? (
                    <p className="hub-terms-content__status hub-terms-content__status--notice">{error}</p>
                  ) : null}

                  <div className="hub-terms-accordion" role="list">
                    {sections.map((section, index) => {
                      const Icon = SECTION_ICONS[section.icon] || FaFileAlt;
                      const isOpen = openIds.has(section.id);
                      return (
                        <article
                          key={section.id}
                          id={`terms-section-${section.id}`}
                          className={`hub-terms-card${isOpen ? " is-open" : ""}`}
                          role="listitem"
                        >
                          <button
                            type="button"
                            className="hub-terms-card__trigger"
                            aria-expanded={isOpen}
                            aria-controls={`terms-panel-${section.id}`}
                            onClick={() => toggleSection(section.id)}
                          >
                            <span className="hub-terms-card__icon" aria-hidden>
                              <Icon />
                            </span>
                            <span className="hub-terms-card__copy">
                              <span className="hub-terms-card__title">
                                {index + 1}. {section.title}
                              </span>
                              {!isOpen && section.summary ? (
                                <span className="hub-terms-card__summary">{section.summary}</span>
                              ) : null}
                            </span>
                            <FaChevronDown className="hub-terms-card__chevron" aria-hidden />
                          </button>
                          <div
                            id={`terms-panel-${section.id}`}
                            className="hub-terms-card__panel"
                            hidden={!isOpen}
                          >
                            <div
                              className="hub-terms-card__body cms-rendered-html"
                              dangerouslySetInnerHTML={{ __html: section.body }}
                            />
                          </div>
                        </article>
                      );
                    })}
                  </div>

                  <div className="hub-terms-callout">
                    <FaInfoCircle className="hub-terms-callout__icon" aria-hidden />
                    <p>{calloutText}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      </main>

      <HubHomeFooter />
    </div>
  );
}
